import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router';
import { signupPasswordPolicy, type OrganizationInvitationDetails } from '@cleopatra/contracts';
import { api, jsonBody } from './api';
import { useAuth } from './auth';
import { PublicHeader } from './public-pages';

export function InvitationPage() {
  const token = window.location.hash.slice(1);
  const { acceptInvitation, confirmInvitation } = useAuth();
  const invitation = useQuery({
    queryKey: ['invitation', token],
    queryFn: () =>
      api<OrganizationInvitationDetails>('/auth/invitations/details', {
        method: 'POST',
        ...jsonBody({ token }),
      }),
    enabled: Boolean(token),
    retry: false,
  });
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [joined, setJoined] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (joined) return <Navigate to="/app" replace />;

  async function accept(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const result = await acceptInvitation(token, { displayName, password });
      if (result.status === 'joined') setJoined(true);
      else setConfirming(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to accept this invitation.');
    } finally {
      setSubmitting(false);
    }
  }

  async function confirm(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const result = await confirmInvitation(token, { displayName, confirmationCode, password });
      if (result.status === 'joined') setJoined(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to confirm this invitation.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="public-site">
      <PublicHeader compact />
      <main className="container py-5">
        <section className="public-signup-card mx-auto">
          {invitation.isLoading ? <p className="mb-0">Loading invitation…</p> : null}
          {invitation.isError ? (
            <div className="alert alert-danger mb-0">
              This invitation is invalid or has expired.
            </div>
          ) : null}
          {invitation.data && !confirming ? (
            <form onSubmit={(event) => void accept(event)}>
              <div className="eyebrow">Organization invitation</div>
              <h1 className="h2">Join {invitation.data.organizationName}</h1>
              <p className="text-muted">
                You were invited as {invitation.data.role.replaceAll('_', ' ')} using{' '}
                {invitation.data.email}.
              </p>
              {error ? <div className="alert alert-danger">{error}</div> : null}
              <label className="form-label" htmlFor="invitation-name">
                Name
              </label>
              <input
                id="invitation-name"
                className="form-control"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                autoComplete="name"
                required
              />
              <label className="form-label mt-3" htmlFor="invitation-password">
                Password
              </label>
              <input
                id="invitation-password"
                className="form-control"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                minLength={8}
                maxLength={signupPasswordPolicy.maxLength}
                required
              />
              <small className="form-text">
                Use your current Cleopatra password, or choose one for your new account.{' '}
                {signupPasswordPolicy.requirements}
              </small>
              <button className="btn btn-primary w-100 mt-4" disabled={submitting}>
                {submitting ? 'Joining…' : 'Join organization'}
              </button>
            </form>
          ) : null}
          {invitation.data && confirming ? (
            <form onSubmit={(event) => void confirm(event)}>
              <div className="eyebrow">Verify email</div>
              <h1 className="h2">Enter your confirmation code</h1>
              <p className="text-muted">We sent a code to {invitation.data.email}.</p>
              {error ? <div className="alert alert-danger">{error}</div> : null}
              <label className="form-label" htmlFor="invitation-code">
                Confirmation code
              </label>
              <input
                id="invitation-code"
                className="form-control form-control-lg"
                value={confirmationCode}
                onChange={(event) => setConfirmationCode(event.target.value)}
                autoComplete="one-time-code"
                required
                autoFocus
              />
              <button className="btn btn-primary w-100 mt-4" disabled={submitting}>
                {submitting ? 'Confirming…' : 'Confirm and join'}
              </button>
            </form>
          ) : null}
        </section>
      </main>
    </div>
  );
}
